import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  useDocumentTaxonomyConfig,
  usePresentationTree,
  useCalculationRules,
  useExtensions,
  useDocumentExtensionSchema,
  useTagsByDocument,
} from 'entities/tag';
import { mergeExtensionsIntoPresentationTree, pruneToUsedConcepts, formatTaxonomyName } from 'app/utils/tag-utils';
import { buildUsedConceptRoles } from 'app/components/editor/sidebar/tags/taxonomy/usedConceptRoles';
import { Typography } from 'app/components/ui';
import Spinner from 'shared/feedback/Spinner';
import { i18n, type MessageKey } from 'shared/i18n';
import { PresentationTreePane } from './panes/PresentationTreePane';
import { CalculationPane } from './panes/CalculationPane';
import { AnchoringPane } from './panes/AnchoringPane';
import { ValuesPane } from './ValuesPane';
import { TagLanguageDropdown } from 'app/components/editor/sidebar/tags/TagLanguageDropdown';

import './TreeOverview.scss';

export const TreeOverview = ({ documentId }: { documentId: string }) => {
  const { t } = useTranslation('tag');

  const documentSuperId = documentId ? parseInt(documentId, 10) : undefined;

  const { data: config, isLoading: configLoading } = useDocumentTaxonomyConfig(documentSuperId);
  const selections = useMemo(() => config?.taxonomySelections ?? [], [config]);

  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<string | undefined>(undefined);
  const [selectedConceptName, setSelectedConceptName] = useState<string | null>(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);

  const handleSelect = (conceptName: string, nodeKey?: string) => {
    setSelectedConceptName(conceptName);
    setSelectedNodeKey(nodeKey ?? null);
  };

  useEffect(() => {
    if (selectedTaxonomyId || selections.length === 0) return;
    const active = selections.find((s) => s.isActive);
    setSelectedTaxonomyId((active ?? selections[0]).taxonomyId);
  }, [selections, selectedTaxonomyId]);

  const { data: presentationTree, isLoading: treeLoading } = usePresentationTree(documentSuperId, selectedTaxonomyId);
  const { data: extensions } = useExtensions(documentSuperId, selectedTaxonomyId);
  const { data: extensionSchema } = useDocumentExtensionSchema(documentSuperId);
  const { data: calculationRules } = useCalculationRules(documentSuperId, selectedTaxonomyId);

  const { data: tagsData } = useTagsByDocument(documentSuperId, { include: ['concept', 'context', 'unit', 'metadata'] });
  const extensionPrefix = extensionSchema?.prefix ?? null;

  const usedConceptRoles = useMemo(() => {
    const used = buildUsedConceptRoles(tagsData?.tags ?? []);
    if (extensionPrefix) {
      for (const [name, roles] of Array.from(used)) {
        if (name.startsWith(`${extensionPrefix}:`)) used.set(name.slice(extensionPrefix.length + 1), roles);
      }
    }
    return used;
  }, [tagsData, extensionPrefix]);

  const usedConceptNames = useMemo(() => new Set(usedConceptRoles.keys()), [usedConceptRoles]);

  const { taxonomyData: mergedTree, extensionConceptNames } = useMemo(
    () => mergeExtensionsIntoPresentationTree(presentationTree, extensions, extensionSchema?.prefix ?? null),
    [presentationTree, extensions, extensionSchema]
  );

  const presentationPreview = useMemo(() => pruneToUsedConcepts(mergedTree, usedConceptNames), [mergedTree, usedConceptNames]);

  if (configLoading) {
    return <Spinner />;
  }

  if (selections.length === 0) {
    return (
      <div className="tree-overview__config-prompt">
        <Typography variant="body-3">{t('treeOverview.noTaxonomy')}</Typography>
      </div>
    );
  }

  const panes: Array<{ titleKey: MessageKey; content: React.ReactNode }> = [
    {
      titleKey: 'tag:treeOverview.pane.presentation',
      content: (
        <PresentationTreePane
          data={presentationPreview}
          extensionTags={extensionConceptNames}
          usedConceptRoles={usedConceptRoles}
          selectedConceptName={selectedConceptName}
          selectedNodeKey={selectedNodeKey}
          onSelect={handleSelect}
        />
      ),
    },
    {
      titleKey: 'tag:treeOverview.pane.calculation',
      content: <CalculationPane rules={calculationRules} selectedNodeKey={selectedNodeKey} onSelect={handleSelect} />,
    },
    {
      titleKey: 'tag:treeOverview.pane.anchoring',
      content: <AnchoringPane extensions={extensions} selectedNodeKey={selectedNodeKey} onSelect={handleSelect} />,
    },
  ];

  return (
    <div className="tree-overview">
      <div className="tree-overview__toolbar">
        <Typography variant="body-7" fontWeight="bold">
          {t('treeOverview.taxonomyLabel')}
        </Typography>
        <select className="tree-overview__select" value={selectedTaxonomyId ?? ''} onChange={(e) => setSelectedTaxonomyId(e.target.value)}>
          {selections.map((s) => (
            <option key={s.taxonomyId} value={s.taxonomyId}>
              {formatTaxonomyName(s.taxonomy?.name ?? s.taxonomyId)}
            </option>
          ))}
        </select>
        <div className="tree-overview__language">
          <TagLanguageDropdown documentSuperId={documentSuperId} />
        </div>
      </div>

      <div className="tree-overview__panes">
        {panes.map((pane) => (
          <section className="tree-overview__pane" key={pane.titleKey}>
            <header className="tree-overview__pane-header">{i18n(pane.titleKey)}</header>
            <div className="tree-overview__pane-body">{treeLoading ? <Spinner /> : pane.content}</div>
          </section>
        ))}
      </div>

      <div className="tree-overview__values">
        <header className="tree-overview__pane-header">{t('treeOverview.pane.values')}</header>
        <div className="tree-overview__values-body">
          <ValuesPane documentSuperId={documentSuperId} selectedConceptName={selectedConceptName} extensionPrefix={extensionPrefix} />
        </div>
      </div>
    </div>
  );
};
